import { provideApiHeaders } from '@/api';

/**
 * The institution whose data a staff member is looking at, picked after signing in and in the
 * top bar. Sent as the X-Institution header; `all` (admins only) means every institution.
 * Remembered per person until they sign out, so the next sign-in asks again.
 */
export const ALL_INSTITUTIONS = 'all';
const PREFIX = 'lousa.institution.';

let current: string | null = null;
provideApiHeaders((): Record<string, string> => (current ? { 'X-Institution': current } : {}));

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function storedInstitution(userId: string): string | null {
  try {
    return storage()?.getItem(PREFIX + userId) ?? null;
  } catch {
    return null;
  }
}

/** Sets the institution sent with every request (and remembers it for this person). */
export function chooseInstitution(userId: string, id: string | null) {
  current = id;
  try {
    if (id) storage()?.setItem(PREFIX + userId, id);
    else storage()?.removeItem(PREFIX + userId);
  } catch {
    // Private windows may refuse storage: the choice just lasts for this page.
  }
}

const TEST_PREFIX = 'lousa.testprofile.';

/** Test accounts: whether they already chose student/teacher since signing in. */
export function testProfileChosen(userId: string): boolean {
  try {
    return storage()?.getItem(TEST_PREFIX + userId) === '1';
  } catch {
    return false;
  }
}

export function rememberTestProfile(userId: string, chosen: boolean) {
  try {
    if (chosen) storage()?.setItem(TEST_PREFIX + userId, '1');
    else storage()?.removeItem(TEST_PREFIX + userId);
  } catch {
    // The choice just lasts for this page.
  }
}

/** On sign-out: forget every choice, so the next sign-in asks again. */
export function forgetInstitutions() {
  current = null;
  try {
    const store = storage();
    if (!store) return;
    Object.keys(store)
      .filter((key) => key.startsWith(PREFIX) || key.startsWith(TEST_PREFIX))
      .forEach((key) => store.removeItem(key));
  } catch {
    // Nothing stored.
  }
}
